import React, { useEffect, useState } from "react";
import { dashApi, tokenStore } from "./api";
import { Breadcrumbs } from "./AdminLayout";
import { TrendingUp, Network, Calendar, FileText, Inbox, Activity, Database, HardDrive, Mail, Archive, Search, Cpu } from "lucide-react";

const HEALTH_ICONS = { database: Database, storage: HardDrive, email: Mail, backup: Archive, search: Search, worker: Cpu };
const STATUS_STYLES = {
  ok: "text-[#3F6250] bg-[#3F6250]/10",
  degraded: "text-[#D97706] bg-[#D97706]/10",
  down: "text-[#9F1239] bg-[#9F1239]/10",
  not_configured: "text-stone2 bg-line/50",
};

export default function AdminDashboard() {
  const [growth, setGrowth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const user = tokenStore.user;
  useEffect(() => {
    dashApi.growth().then(setGrowth).catch(()=>{});
    dashApi.summary().then(setSummary).catch(()=>{});
    dashApi.health().then(setHealth).catch(()=>{});
  }, []);
  return (
    <div data-testid="admin-dashboard">
      <Breadcrumbs items={["Admin", "Executive Dashboard"]}/>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-prestige">Welcome back</div>
          <h1 className="font-serif text-4xl text-navy mt-2 tracking-tight">{user?.name || "Doctor"}</h1>
          <p className="text-stone2 mt-2 text-sm">Your platform, at a glance.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <StatCard testid="stat-new-enquiries" icon={Inbox} label="New Enquiries" value={summary?.new_enquiries ?? "—"}/>
        <StatCard testid="stat-appointments" icon={Calendar} label="Appointment Requests" value={summary?.new_appointments ?? "—"}/>
        <StatCard testid="stat-drafts" icon={FileText} label="Drafts" value={summary?.drafts ?? "—"} note="M2 activates full drafts"/>
      </div>

      <section className="bg-white border border-line p-8 mb-8" data-testid="knowledge-growth">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} strokeWidth={1.5} className="text-prestige"/>
          <div className="mono text-[10px] uppercase tracking-widest text-prestige">Knowledge Growth</div>
        </div>
        <h2 className="font-serif text-2xl text-navy mt-2">Platform, by the numbers.</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6">
          {(growth?.metrics || []).map(m => (
            <div key={m.key} data-testid={`kg-${m.key}`} className="border border-line p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-stone2">{m.label}</div>
              <div className="font-serif text-3xl text-navy mt-1 tracking-tight">{m.total}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center gap-6 border-t border-line pt-4 flex-wrap">
          <Network size={16} strokeWidth={1.5} className="text-prestige"/>
          <div className="mono text-[11px] uppercase tracking-widest text-stone2">Knowledge Graph:</div>
          <div className="text-sm text-navy"><span className="font-medium">{growth?.graph?.edges ?? 0}</span> verified connections</div>
          <div className="text-sm text-stone2"><span className="font-medium">{growth?.graph?.unlinked_entities ?? 0}</span> unlinked entities</div>
        </div>
      </section>

      <section className="bg-white border border-line p-8 mb-8" data-testid="system-health">
        <div className="flex items-center gap-2">
          <Activity size={16} strokeWidth={1.5} className="text-prestige"/>
          <div className="mono text-[10px] uppercase tracking-widest text-prestige">System Health</div>
        </div>
        <h2 className="font-serif text-2xl text-navy mt-2">Every service, live-checked.</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
          {(health?.services || []).map(s => {
            const Icon = HEALTH_ICONS[s.key] || Activity;
            const st = STATUS_STYLES[s.status] || STATUS_STYLES.degraded;
            return (
              <div key={s.key} data-testid={`health-${s.key}`} className="border border-line p-4 flex items-start gap-3">
                <div className="w-9 h-9 bg-clinical text-navy flex items-center justify-center shrink-0"><Icon size={16} strokeWidth={1.5}/></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-navy font-medium text-sm">{s.label}</div>
                    <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${st}`}>{s.status.replace('_',' ')}</span>
                  </div>
                  <div className="text-xs text-stone2 mt-1 truncate" title={s.note}>{s.note}</div>
                  {s.latency_ms !== undefined && <div className="mono text-[10px] text-stone2 mt-0.5">{s.latency_ms} ms</div>}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-line p-6">
          <div className="mono text-[10px] uppercase tracking-widest text-prestige">Recent Articles</div>
          <ul className="mt-4 divide-y divide-line">
            {(summary?.recent_articles || []).map((a, i) => (
              <li key={i} className="py-3">
                <div className="text-sm text-navy font-medium">{a.title}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-stone2 mt-1">{a.category} · {a.published_at?.slice(0,10)}</div>
              </li>
            ))}
            {(!summary?.recent_articles || summary.recent_articles.length === 0) && <li className="py-3 text-sm text-stone2">No published articles yet.</li>}
          </ul>
        </div>
        <div className="bg-white border border-line p-6">
          <div className="mono text-[10px] uppercase tracking-widest text-prestige">Recent Enquiries</div>
          <ul className="mt-4 divide-y divide-line">
            {(summary?.recent_enquiries || []).map((e, i) => (
              <li key={i} className="py-3">
                <div className="text-sm text-navy font-medium">{e.name} <span className="text-stone2 font-normal">· {e.type}</span></div>
                <div className="mono text-[10px] uppercase tracking-widest text-stone2 mt-1">{e.status} · {e.created_at?.slice(0,10)}</div>
              </li>
            ))}
            {(!summary?.recent_enquiries || summary.recent_enquiries.length === 0) && <li className="py-3 text-sm text-stone2">No enquiries yet.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, note, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-line p-5 flex items-center gap-4">
      <div className="w-10 h-10 bg-clinical flex items-center justify-center text-navy"><Icon size={18} strokeWidth={1.5}/></div>
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-stone2">{label}</div>
        <div className="font-serif text-3xl text-navy mt-0.5">{value}</div>
        {note && <div className="text-[10px] text-stone2 mt-0.5">{note}</div>}
      </div>
    </div>
  );
}
